import {
  ChatBubbleOutline as CommentIcon,
  ChatBubble as MessageIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ReviewSectionConversation } from '../../models/ContentReviewInterface';
import { Palette } from 'Palette';
import {
  commentOverlayPointerStyle,
  computeCommentOverlayPosition,
  OverlayPosition,
} from './commentIconOverlayPosition';

interface CommentIconProps {
  conversation?: ReviewSectionConversation;
  conversations?: ReviewSectionConversation[];
  tone?: keyof typeof Palette;
  currentUser?: string;
  onReply?: (conversationId: string, message: string) => void;
  showAddBadge?: boolean;
  onClick?: () => void;
}

const CommentIconComponent: React.FC<CommentIconProps> = ({
  conversation,
  conversations,
  tone = 'default',
  currentUser,
  onReply,
  showAddBadge = false,
  onClick,
}) => {
  const allConversations = conversations || (conversation ? [conversation] : []);
  const totalComments = allConversations.reduce((sum, conv) => sum + conv.comments.length, 0);

  const [isOpen, setIsOpen] = useState(false);
  const [chatBoxPosition, setChatBoxPosition] = useState<OverlayPosition | null>(null);
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && iconRef.current) {
      setChatBoxPosition(computeCommentOverlayPosition(iconRef.current));
    }
  }, [isOpen]);

  const handleIconClick = () => {
    if (totalComments > 0) {
      setIsOpen(!isOpen);
    } else if (onClick) {
      onClick();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setShowReplyForm(false);
    setReplyText('');
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim() || !onReply || !currentUser || allConversations.length === 0) {
      return;
    }

    setSubmitting(true);
    try {
      await onReply(allConversations[0].conversationId, replyText);
      setReplyText('');
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplyCancel = () => {
    setShowReplyForm(false);
    setReplyText('');
  };

  return (
    <>
      <Box
        ref={iconRef}
        sx={{
          position: 'relative',
          display: 'inline-block',
        }}
      >
        <Badge
          badgeContent={totalComments > 0 ? totalComments : showAddBadge ? '+' : undefined}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: Palette[tone].buttonBg,
              color: 'white',
            },
          }}
        >
          <IconButton
            size="small"
            onClick={handleIconClick}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: `2px solid ${Palette[tone].buttonBg}`,
              '&:hover': {
                backgroundColor: Palette[tone].buttonBg,
                color: 'white',
              },
            }}
          >
            <CommentIcon fontSize="small" />
          </IconButton>
        </Badge>
      </Box>
      {chatBoxPosition && isOpen && (
        <Paper
          elevation={10}
          className="comment-overlay-paper"
          sx={{
            position: 'absolute',
            top: chatBoxPosition.top,
            left: chatBoxPosition.left,
            width: 320,
            maxHeight: 320,
            p: 2,
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            transform: 'none',
            display: 'flex',
            zIndex: 21,
            flexDirection: 'column',
            '&::before': commentOverlayPointerStyle(chatBoxPosition) as object,
          }}
        >
          <Stack spacing={1.25} sx={{ flex: 1, overflowY: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MessageIcon fontSize="small" sx={{ color: Palette[tone].buttonBg }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: Palette[tone].buttonBg,
                    fontSize: '0.85rem',
                  }}
                >
                  Conversation ({totalComments} messages)
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  p: 0.5,
                  minWidth: 'auto',
                  color: Palette[tone].text,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                  },
                }}
              >
                ×
              </IconButton>
            </Box>
            <Divider sx={{ opacity: 0.5 }} />

            <Stack spacing={1.5} sx={{ maxHeight: 200, overflowY: 'auto' }}>
              {allConversations.flatMap((conv, convIndex) =>
                conv.comments.map((message, index) => (
                  <Box key={`${convIndex}-${index}`}>
                    <Box
                      sx={{
                        bgcolor: index === 0 ? 'rgba(232,245,233,0.7)' : 'rgba(240,240,240,0.7)',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1,
                        color: index === 0 ? '#245d2a' : '#333',
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)',
                        border:
                          index === 0 ? `1px solid ${Palette[tone].buttonBg}` : '1px solid #e0e0e0',
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {message.submittedBy.name}
                      </Typography>
                      <Typography variant="body2">{message.message}</Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.7rem',
                        display: 'block',
                        textAlign: 'right',
                        mt: 0.5,
                      }}
                    >
                      {new Date(message.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                )),
              )}
            </Stack>

            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: '1px solid #e0e0e0',
                backgroundColor: 'rgba(0,0,0,0.02)',
                borderRadius: 1,
                p: 1,
              }}
            >
              {showReplyForm ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    sx={{
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                      },
                    }}
                    autoFocus
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={handleReplyCancel}
                      disabled={submitting}
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      key={replyText}
                      variant="contained"
                      onClick={handleReplySubmit}
                      disabled={!replyText.trim() || submitting}
                      startIcon={<ReplyIcon />}
                      sx={{
                        backgroundColor: Palette[tone].buttonBg,
                        '&:hover': {
                          backgroundColor: Palette[tone].buttonBg,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {submitting ? 'Sending...' : 'Reply'}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Button
                  size="small"
                  startIcon={<ReplyIcon />}
                  onClick={() => setShowReplyForm(true)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Reply
                </Button>
              )}
            </Box>
          </Stack>
        </Paper>
      )}
    </>
  );
};

CommentIconComponent.displayName = 'CommentIcon';

export default CommentIconComponent;
